import { useEffect, useState, useCallback, useMemo } from "react";
import { useMsal } from "@azure/msal-react";
import { CheckCircle, Circle } from "lucide-react";
import { callApi } from "../api/api";
import { notifyAuthContextChanged } from "../utils/authCache";
import ResponsiveTableCards from "../components/ResponsiveTableCards";
import ScrollableTable from "../components/ScrollableTable";
import ManagePageHeader from "../components/ManagePageHeader";

const isResourceActive = (resource) =>
  resource?.is_active !== false && resource?.is_active !== 0;

const isRoleActive = (role) =>
  role?.is_active !== false && role?.is_active !== 0;

const isRoleResourceAssignmentFrozen = (role, resource) =>
  !isRoleActive(role) || !isResourceActive(resource);

const getResourceStatusLabel = (resource) =>
  isResourceActive(resource) ? "Active" : "Inactive";

const FROZEN_ASSIGNMENT_TOOLTIP =
  "Assignments are frozen while the role or resource is inactive.";

export default function AssignRoleResource() {
  const { instance, accounts } = useMsal();


  const [roles, setRoles] = useState([]);
  const [resources, setResources] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [isNarrow, setIsNarrow] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 960px)");
    const update = () => setIsNarrow(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const [draft, setDraft] = useState({});
  const [initial, setInitial] = useState({});

  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  /* ================= LOAD ================= */
  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      const [rolesRes, resRes] = await Promise.all([
        callApi(instance, accounts, "GET", "/api/admin/roles"),
        callApi(instance, accounts, "GET", "/api/admin/resources"),
      ]);

      const roleList = rolesRes?.data?.data || [];
      const resourceList = resRes?.data?.data || [];

      setRoles(roleList);
      setResources(resourceList);

      if (roleList.length) setSelectedRole(roleList[0]);
    } catch {
      showToast("Failed to load", "error");
    } finally {
      setLoading(false);
    }
  }, [instance, accounts]);

  /* ================= LOAD PERMISSIONS ================= */
  const loadPermissions = useCallback(
    async (roleId) => {
      const res = await callApi(
        instance,
        accounts,
        "GET",
        `/api/admin/role-resources?roleId=${roleId}`,
      );

      const map = {};

      (res?.data?.data || []).forEach((r) => {
        map[r.resource_id] = true;
      });

      setDraft(map);
      setInitial(map);
    },
    [instance, accounts],
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (selectedRole) {
      loadPermissions(selectedRole.role_id);
    }
  }, [selectedRole, loadPermissions]);

  /* ================= TOGGLE ================= */
  const toggle = useCallback(
    (id) => {
      const resource = resources.find((item) => item.resource_id === id);
      if (
        resource &&
        isRoleResourceAssignmentFrozen(selectedRole, resource)
      ) {
        return;
      }

      setDraft((prev) => ({
        ...prev,
        [id]: !prev[id],
      }));
    },
    [resources, selectedRole],
  );

  /* ================= SAVE ================= */
  const handleSave = async () => {
    try {
      setSaving(true);

      const toAssign = [];
      const toRemove = [];

      resources.forEach((r) => {
        if (isRoleResourceAssignmentFrozen(selectedRole, r)) {
          return;
        }

        const was = !!initial[r.resource_id];
        const now = !!draft[r.resource_id];

        if (!was && now) toAssign.push(r.resource_id);
        if (was && !now) toRemove.push(r.resource_id);
      });

      await Promise.all([
        ...toAssign.map((id) =>
          callApi(instance, accounts, "POST", "/api/admin/assign-resource", {
            roleId: selectedRole.role_id,
            resourceId: id,
          }),
        ),
        ...toRemove.map((id) =>
          callApi(instance, accounts, "POST", "/api/admin/remove-resource", {
            roleId: selectedRole.role_id,
            resourceId: id,
          }),
        ),
      ]);

      showToast("Permissions updated");
      notifyAuthContextChanged();
      await loadPermissions(selectedRole.role_id);
    } catch {
      showToast("Save failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const filteredRoles = useMemo(() => {
    return roles.filter((r) =>
      r.role_name?.toLowerCase().includes(search.toLowerCase()),
    );
  }, [roles, search]);

  /* ================= RESPONSIVE CARD MAPPERS ================= */

  const mapResourceCard = useCallback(
    (r) => {
      const granted = !!draft[r.resource_id];
      const statusLabel = getResourceStatusLabel(r);
      const frozen = isRoleResourceAssignmentFrozen(selectedRole, r);

      return {
        id: `resource-${r.resource_id}`,
        icon: granted ? <CheckCircle size={16} /> : <Circle size={16} />,
        tone: granted ? "success" : undefined,
        title: r.resource_code,
        subtitle: r.resource_name || "—",
        meta: [
          { label: "Access", value: r.access_level_code || "—" },
          { label: "Status", value: statusLabel },
        ],
        badges: [
          {
            value: granted ? "Granted" : "Not Granted",
            tone: granted ? "success" : undefined,
          },
          ...(frozen
            ? [{ value: "Assignments frozen", tone: "warning" }]
            : []),
        ],
        actions: frozen
          ? []
          : [
              {
                label: granted ? "Revoke" : "Grant",
                icon: granted ? <CheckCircle size={14} /> : <Circle size={14} />,
                variant: granted ? "secondary" : undefined,
                onClick: () => toggle(r.resource_id),
              },
            ],
      };
    },
    [draft, selectedRole, toggle],
  );

  const mapResourceDrawer = useCallback(
    (r) => {
      const granted = !!draft[r.resource_id];
      const frozen = isRoleResourceAssignmentFrozen(selectedRole, r);

      return {
        title: r.resource_code,
        fields: [
          { label: "Resource Name", value: r.resource_name || "—" },
          { label: "Access Level", value: r.access_level_code || "—" },
          { label: "Status", value: getResourceStatusLabel(r) },
          { label: "Permission", value: granted ? "Granted" : "Not Granted" },
          ...(frozen
            ? [
                {
                  label: "Note",
                  value:
                    "Assignments are frozen while the role or resource is inactive.",
                },
              ]
            : []),
        ],
        primaryAction: frozen
          ? undefined
          : {
              label: granted ? "Revoke Access" : "Grant Access",
              onClick: () => toggle(r.resource_id),
            },
      };
    },
    [draft, selectedRole, toggle],
  );

  const renderResourcesTable = () => (
    <ScrollableTable style={styles.tableWrap}>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.thCenter}>GRANTED</th>
            <th style={styles.th}>CODE</th>
            <th style={styles.th}>ACCESS</th>
            <th style={styles.th}>STATUS</th>
          </tr>
        </thead>

        <tbody>
          {resources.map((r) => {
            const exists = !!draft[r.resource_id];
            const frozen = isRoleResourceAssignmentFrozen(selectedRole, r);

            return (
              <tr
                key={r.resource_id}
                style={{
                  ...styles.row,
                  ...(frozen ? styles.frozenRow : null),
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "#f8fafc")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = frozen ? "#fafafa" : "#fff")
                }
              >
                <td style={styles.tdCenter}>
                  <input
                    type="checkbox"
                    style={{
                      ...styles.checkbox,
                      ...(frozen ? styles.checkboxDisabled : null),
                    }}
                    checked={exists}
                    disabled={frozen}
                    title={frozen ? FROZEN_ASSIGNMENT_TOOLTIP : undefined}
                    onChange={() => toggle(r.resource_id)}
                  />
                </td>

                <td style={styles.td}>
                  <span style={styles.resourceCode}>
                    {r.resource_code}
                  </span>
                </td>

                <td style={styles.td}>
                  <span style={styles.badge}>
                    {r.access_level_code}
                  </span>
                </td>

                <td style={styles.td}>
                  <span
                    style={
                      isResourceActive(r) ? styles.active : styles.inactive
                    }
                  >
                    ● {getResourceStatusLabel(r)}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </ScrollableTable>
  );

  if (loading) return <div className="page-container" style={styles.container}>Loading...</div>;

  return (
    <div className="page-container" style={{ ...styles.container, ...(isNarrow ? styles.containerNarrow : null) }}>
      <ManagePageHeader pageTitle="Role-Resource Assign" />
      <div style={styles.title}>
        <h2>Role-Resource Assignments</h2>

        <p style={styles.subtitle}>
          Configure resource access and permissions for each system role.
        </p>
      </div>

      <div style={{ ...styles.layout, ...(isNarrow ? styles.layoutNarrow : null) }}>
        {/* LEFT */}
        <div style={{ ...styles.left, ...(isNarrow ? styles.leftNarrow : null) }}>
          <input
            placeholder="Search roles..."
            style={styles.search}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div style={styles.roleList} className="scrollable">
            {filteredRoles.map((role) => (
              <div
                key={role.role_id}
                style={{
                  ...styles.roleItem,
                  ...(selectedRole?.role_id === role.role_id
                    ? styles.roleSelected
                    : styles.roleNormal),
                }}
                onClick={() => setSelectedRole(role)}
              >
                <>
                  <div style={styles.roleName}>{role.role_name}</div>

                  <div style={styles.code}>{role.role_code}</div>
                  {!isRoleActive(role) ? (
                    <div style={styles.roleInactiveNote}>● Inactive role</div>
                  ) : null}
                </>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT */}
        <div style={{ ...styles.right, ...(isNarrow ? styles.rightNarrow : null) }}>
          {selectedRole && (
            <div style={styles.rightInner}>
              <div style={styles.selectedHeader}>
                <div style={styles.selectedTitle}>{selectedRole.role_name}</div>

                <div style={styles.selectedSub}>
                  {isRoleActive(selectedRole)
                    ? "Manage granted resources and access levels"
                    : "Assignments are frozen while this role is inactive."}
                </div>
              </div>

              <div style={styles.tableArea}>
                <ResponsiveTableCards
                  breakpoint={960}
                  data={resources}
                  loading={false}
                  emptyTitle="No Resources Available"
                  emptySub="No resources have been configured yet."
                  listTitle="Available Resources"
                  listSubtitle={`Tap a resource to toggle access for ${selectedRole.role_name}`}
                  cardMapper={mapResourceCard}
                  drawerMapper={mapResourceDrawer}
                  renderTable={renderResourcesTable}
                />
              </div>

              <div style={styles.footer}>
                <button
                  style={{
                    ...styles.saveBtn,
                    opacity: saving ? 0.7 : 1,
                    cursor: saving ? "not-allowed" : "pointer",
                  }}
                  onMouseEnter={(e) =>
                    !saving &&
                    (e.currentTarget.style.transform = "translateY(-1px)")
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.transform = "translateY(0)")
                  }
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {toast && (
        <div
          style={{
            ...styles.toast,
            background: toast.type === "success" ? "#16a34a" : "#ef4444",
          }}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    background: "#f8fafc",
    minHeight: "80vh",
  },

  containerNarrow: {
    padding: "12px",
  },

  title: {
    marginBottom: "24px",
  },

  subtitle: {
    color: "#64748b",
    marginTop: "4px",
    fontSize: "14px",
  },

  /* ================= LAYOUT ================= */

  layout: {
    display: "grid",
    gridTemplateColumns: "340px 1fr",
    gap: "24px",
    alignItems: "stretch",
  },

  layoutNarrow: {
    gridTemplateColumns: "1fr",
    gap: "16px",
  },

  /* ================= LEFT ================= */

  left: {
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: "18px",
    padding: "18px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    height: "calc(100vh - 180px)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },

  leftNarrow: {
    height: "auto",
    maxHeight: "340px",
  },

  search: {
    width: "100%",
    padding: "12px 14px",
    borderRadius: "10px",
    border: "1px solid #dbe2ea",
    fontSize: "14px",
    outline: "none",
    marginBottom: "18px",
    boxSizing: "border-box",
    transition: "all 0.2s ease",
  },

  roleList: {
    overflowY: "auto",
    overflowX: "hidden",
    flex: 1,
    paddingRight: "4px",
  },

  roleItem: {
    padding: "14px 16px",
    borderRadius: "14px",
    marginBottom: "10px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    border: "1px solid transparent",
  },

  roleSelected: {
    background: "#eef2ff",
    border: "1px solid #c7d2fe",
  },

  roleNormal: {
    background: "#fff",
    border: "1px solid #f1f5f9",
  },

  roleName: {
    fontWeight: "600",
    color: "#0f172a",
    marginBottom: "6px",
    fontSize: "14px",
  },

  code: {
    fontFamily: "monospace",
    fontSize: "12px",
    color: "#64748b",
  },

  /* ================= RIGHT ================= */

  right: {
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: "18px",
    padding: "24px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    height: "calc(100vh - 180px)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },

  rightNarrow: {
    height: "auto",
  },

  rightInner: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
    height: "100%",
  },

  tableArea: {
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },

  selectedHeader: {
    marginBottom: "24px",
    paddingBottom: "18px",
    borderBottom: "1px solid #f1f5f9",
    flexShrink: 0,
  },

  selectedTitle: {
    fontSize: "18px",
    fontWeight: "700",
    color: "#0f172a",
    marginBottom: "6px",
  },

  selectedSub: {
    fontSize: "13px",
    color: "#64748b",
  },

  /* ================= TABLE ================= */

  tableWrap: {
    overflowX: "auto",
  },

  table: {
    width: "100%",
    borderCollapse: "separate",
    borderSpacing: 0,
  },

  th: {
    textAlign: "center",
    padding: "16px 18px",
    fontSize: "12px",
    fontWeight: "700",
    letterSpacing: "0.05em",
    color: "#64748b",
    background: "#f8fafc",
    borderBottom: "1px solid #e2e8f0",
    whiteSpace: "nowrap",
  },

  thCenter: {
    textAlign: "center",
    width: "120px",
    padding: "16px 18px",
    fontSize: "12px",
    fontWeight: "700",
    letterSpacing: "0.05em",
    color: "#64748b",
    background: "#f8fafc",
    borderBottom: "1px solid #e2e8f0",
  },

  row: {
    transition: "background 0.2s ease",
  },

  td: {
    padding: "18px",
    borderBottom: "1px solid #f8fafc",
    verticalAlign: "middle",
    fontSize: "14px",
  },

  tdCenter: {
    textAlign: "center",
    padding: "18px",
    borderBottom: "1px solid #f8fafc",
    verticalAlign: "middle",
  },

  resourceCode: {
    fontWeight: "600",
    color: "#0f172a",
    fontFamily: "monospace",
    fontSize: "13px",
  },

  badge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px 12px",
    borderRadius: "999px",
    fontSize: "12px",
    fontWeight: "600",
    background: "#dbeafe",
    color: "#1d4ed8",
  },

  active: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    color: "#15803d",
    background: "#dcfce7",
    padding: "6px 10px",
    borderRadius: "999px",
    fontSize: "12px",
    fontWeight: "600",
  },

  inactive: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    color: "#dc2626",
    background: "#fee2e2",
    padding: "6px 10px",
    borderRadius: "999px",
    fontSize: "12px",
    fontWeight: "600",
  },

  checkbox: {
    width: "18px",
    height: "18px",
    cursor: "pointer",
    accentColor: "#0f172a",
  },

  checkboxDisabled: {
    cursor: "not-allowed",
    opacity: 0.55,
  },

  frozenRow: {
    background: "#fafafa",
  },

  roleInactiveNote: {
    marginTop: "6px",
    fontSize: "11px",
    fontWeight: "600",
    color: "#dc2626",
  },

  /* ================= FOOTER ================= */

  footer: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: "24px",
    paddingTop: "20px",
    borderTop: "1px solid #f1f5f9",
    flexShrink: 0,
  },

  saveBtn: {
    background: "#0f172a",
    color: "#fff",
    border: "none",
    padding: "12px 18px",
    borderRadius: "10px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.2s ease",
    minWidth: "160px",
  },

  /* ================= TOAST ================= */

  toast: {
    position: "fixed",
    bottom: "24px",
    right: "24px",
    padding: "14px 18px",
    borderRadius: "12px",
    color: "#fff",
    fontWeight: "500",
    boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
    zIndex: 1000,
  },
};